import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import {
  TaskHeader,
  TaskSessionComplete,
  TaskShell,
  TaskStepDots,
  kitLang,
  taskPercent,
  taskSteps,
} from '@/features/contribution-kit';
import {
  ValidateAudioSetup,
  ValidateAudioItemCard,
  ValidateAudioLanguageRow,
  ValidateAudioDecisionPanel,
  ValidateAudioActionBar,
  validateAudioCopy,
  useValidateAudioSession,
  useAudioTextCorrection,
  canCorrectAudioText,
} from '@/features/validate-audio';
import { ValidationGateProgressStrip } from '@/features/validation-gate';

/** "Valider les audios": listen to other contributors' recordings and judge them. */
export default function ValidateAudio() {
  const { t, language } = useApp();
  const { toast } = useToast();
  const copy = validateAudioCopy[kitLang(language)];
  const session = useValidateAudioSession();
  const textCorrection = useAudioTextCorrection((result) =>
    session.applyAudioTexts(result.id, { phraseLocal: result.phraseLocal, phraseFr: result.phraseFr }));
  const s = (key: string) => String((t as Record<string, unknown>)[key] ?? '');

  if (!session.sessionStarted) {
    return (
      <ValidateAudioSetup
        t={t}
        totalValidations={session.totalValidations}
        languages={session.languages}
        selectedLanguage={session.selectedLanguage}
        onLanguageChange={session.setSelectedLanguage}
        isLoading={session.isLoading}
        onStart={session.startLinkedSession}
        onBack={session.goHome}
      />
    );
  }

  if (session.sessionComplete) {
    return (
      <TaskSessionComplete
        title={s('validationCompleteTitle')}
        description={s('validationCompleteSubtitle')}
        stats={[
          { label: s('validationCompleteValidated'), value: session.totalValidations },
          { label: s('validationCompleteAccepted'), value: session.acceptedCount, tone: 'success' },
          { label: s('validationCompleteRejected'), value: session.rejectedCount, tone: session.rejectedCount > 0 ? 'danger' : 'default' },
        ]}
        continueLabel={s('validationCompleteContinue')}
        homeLabel={s('back')}
        hint={s('validationCompleteContinueHint')}
        onContinue={session.restartValidation}
        onHome={session.goHome}
      />
    );
  }

  const current = session.currentAudioData;
  const decided = session.acceptedCount + session.rejectedCount;
  const accuracy = taskPercent(session.acceptedCount, decided);
  const steps = taskSteps(session.totalAudios, session.currentAudio, (index) => {
    const audio = session.pendingAudios[index];
    return audio?.hasValidated ? 'done' : audio?.hasRejected ? 'rejected' : null;
  });

  return (
    <TaskShell
      width="wide"
      header={
        <TaskHeader
          task="verifyAudio"
          index={session.currentAudio}
          total={session.totalAudios}
          positionLabel={s('audioXofY')
            .replace('{current}', String(Math.min(session.currentAudio + 1, session.totalAudios)))
            .replace('{total}', String(session.totalAudios))}
          progress={session.sessionProgress}
          itemId={current?.id}
          onBack={session.goHome}
          meta={<span>{session.selectedLanguage}</span>}
        />
      }
      notices={<ValidationGateProgressStrip />}
      actionBar={
        <ValidateAudioActionBar
          t={t}
          copy={copy}
          isFirst={session.currentAudio === 0}
          isLast={session.currentAudio === session.totalAudios - 1}
          isSubmitting={session.isSubmitting}
          hasAudio={current !== null}
          shortcutsEnabled
          onPrevious={session.previousAudio}
          onReject={session.rejectAudio}
          onAccept={session.acceptAudio}
          onNext={session.moveToNext}
          onTogglePlay={session.playAudio}
        />
      }
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:items-start">
        {current ? (
          <ValidateAudioItemCard
            t={t}
            current={current}
            isPlaying={session.isPlaying}
            audioRef={session.audioRef}
            canvasRef={session.canvasRef}
            onPlayAudio={session.playAudio}
            onAudioEnded={session.handleAudioEnded}
            hasValidated={session.hasValidated}
            hasRejected={session.hasRejected}
            canDelete={session.user?.role === 'moderator'}
            isDeleting={session.isDeleting}
            onDeleteAudio={session.deleteCurrentAudio}
            textCorrection={canCorrectAudioText(session.user?.role) ? {
              copy: t.audioTextCorrection,
              isSaving: textCorrection.isPending,
              onSave: async (request) => {
                if (!session.currentAudioData) return;
                await textCorrection.mutateAsync({ audioId: session.currentAudioData.id, request });
              },
            } : undefined}
            languageRow={
              <ValidateAudioLanguageRow
                t={t}
                selectedLanguage={session.selectedLanguage}
                confirmedLanguage={session.confirmedLanguage}
                showLanguageConfirmation={session.showLanguageConfirmation}
                languages={session.languages}
                onToggleLanguageConfirmation={() => session.setShowLanguageConfirmation(!session.showLanguageConfirmation)}
                onConfirmedLanguageChange={(value) => {
                  session.setConfirmedLanguage(value);
                  toast({
                    title: s('languageCorrected'),
                    description: s('languageChangedTo').replace('{value}', value),
                  });
                  session.setShowLanguageConfirmation(false);
                }}
              />
            }
            footer={
              <div className="flex flex-wrap items-center justify-between gap-3">
                <TaskStepDots
                  steps={steps}
                  labels={{ current: s('sessionInProgress'), done: copy.stepAccepted, rejected: copy.stepRejected, todo: s('waiting') }}
                />
                {decided > 0 ? (
                  <p className="text-xs text-muted-foreground">{s('currentAccuracy').replace('{accuracy}', String(accuracy))}</p>
                ) : null}
              </div>
            }
          />
        ) : (
          <div />
        )}

        <ValidateAudioDecisionPanel
          t={t}
          copy={copy}
          audioGender={session.audioGender}
          onAudioGenderChange={session.setAudioGender}
          rejectionReason={session.rejectionReason}
          onRejectionReasonChange={session.setRejectionReason}
          detailedRejectionReason={session.detailedRejectionReason}
          onDetailedRejectionReasonChange={session.setDetailedRejectionReason}
          onReRecord={session.navigateToReRecord}
        />
      </div>
    </TaskShell>
  );
}
